import React, { memo, useMemo } from "react";
import FormFooter from "../form-footer/form-footer";
import { Props } from "../page/page";
import SubheaderBase from "../subheader/subheader-base";
import PageLayout from "../page-layout";
import ScrollableContent from "../scrollable-content";
import AvatarHeader from "../avatar-header/avatar-header";

// Props for AvatarHeader when passed via headerProps
type AvatarHeaderProps = {
  titles?: string[];
  avatarName?: string;
  avatarSpriteId?: number;
};

type FormProps = Props & {
  onSubmit: () => void;

  loading?: boolean;
  ready?: boolean;

  submitText?: string;

  loadingText?: string;

  showSkip?: boolean;
  hideConnected?: boolean;
  readyFlash?: boolean;

  // New: Pass header props instead of a pre-created element
  // This allows FormPage to memoize the header internally
  headerProps?: AvatarHeaderProps;
};

// Memoized header component - only re-renders when its own props change
const MemoizedAvatarHeader = memo(AvatarHeader);

const FormPage: React.FunctionComponent<FormProps> = (props) => {
  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    props.onSubmit();
  }

  // Memoize the header element based on headerProps
  // This ensures the header doesn't re-render when form state changes
  const headerElement = useMemo(() => {
    // Prefer headerProps if provided (new pattern)
    if (props.headerProps) {
      return (
        <MemoizedAvatarHeader
          titles={props.headerProps.titles}
          avatarName={props.headerProps.avatarName}
          avatarSpriteId={props.headerProps.avatarSpriteId}
        />
      );
    }
    // Fall back to header prop for backward compatibility
    return props.header;
  }, [props.headerProps, props.header]);

  return (
    <PageLayout as="form" onSubmit={handleSubmit}>
      {headerElement}
      <SubheaderBase subheaders={props.subHeaders} />

      <ScrollableContent>{props.children}</ScrollableContent>

      <FormFooter
        readyFlash={props.readyFlash}
        ready={props.ready ?? true}
        loadingText={props.loadingText}
        error={props.error}
        countdown={props.countdown}
        countdownThreshold={props.countdownThreshold}
        loading={props.loading}
        onBack={props.onBack}
        onSubmit={handleSubmit}
        submitText={props.submitText}
      />
    </PageLayout>
  );
};

export default FormPage;

